import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { PaperButtonComponent, PaperKbdComponent, PaperTagComponent } from '@ricoh-paper/angular';
import { LocaleService } from '../../../../../core/services/locale.service';
import type { CmdkNewsItem } from '../models/cmdk.model';

/**
 * The preview for an article, announcement, video or knowledge page.
 *
 * This is the one preview with a real hero image, because it is the one kind
 * whose records actually carry one. It is rendered only when present: a
 * placeholder would make every knowledge page look like an article with a
 * broken cover.
 *
 * The summary is the article's own description — not a generated one. The
 * palette has an AI surface and it is the Ask row; quietly summarising an
 * article here would blur the line the whole design keeps between "this is
 * what the system knows" and "this is what a model said".
 */
@Component({
  selector: 'im-cmdk-preview-news',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperButtonComponent, PaperKbdComponent, PaperTagComponent],
  templateUrl: './cmdk-preview-news.component.html',
  styleUrl: './cmdk-preview-news.component.scss',
})
export class CmdkPreviewNewsComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  readonly item = input.required<CmdkNewsItem>();
  readonly activated = output<void>();

  protected readonly typeLabel = computed(() => {
    this.locale.locale();
    // A Spotlight's word is its product name, in the Spotlight namespace.
    const type = this.item().newsType;
    return this.transloco.translate(
      type === 'spotlight' ? 'spotlight.places.search.type' : `shell.cmdk.preview_news.type_${type}`,
    );
  });

  protected readonly readTime = computed(() => {
    this.locale.locale();
    const minutes = this.item().readTimeMinutes;
    return minutes
      ? this.transloco.translate('shell.cmdk.preview_news.read_time', { count: minutes })
      : null;
  });

  protected readonly summaryLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('shell.cmdk.preview_news.summary');
  });

  protected readonly openLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('shell.cmdk.preview_news.open');
  });
}
